import './coapplicantResidenceDurationPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import { ONE_YEAR_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/oneYear';
import { TWO_YEARS_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/twoYears';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    computed: { isCoapplicantPersisted },
    coapplicantCurrentResidenceMonthsAt,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentResidenceMonthsAt,
    syncCoapplicant,
  } = useCoapplicantStore();

  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      monthsAtResidenceValue: coapplicantCurrentResidenceMonthsAt,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<string>): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const monthsAtResidenceValue = event.target.value;

    setIsInitialSelection(
      isEmpty(coapplicantCurrentResidenceMonthsAt) && isValidClickEvent(event),
    );

    setCoapplicantCurrentResidenceMonthsAt(monthsAtResidenceValue);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { monthsAtResidenceValue } = values;

    if (!monthsAtResidenceValue) {
      return;
    }

    if (isCoapplicantPersisted) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  useEffect((): void => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <div className="page coapplicant-residence-duration-page">
        <Header
          progress={progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantResidenceDuration.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={locator.pages.coapplicantResidenceDuration.form.h1}
              >
                {t('CoapplicantResidenceDurationPage.title')}
              </h1>
              <h2
                data-testid={locator.pages.coapplicantResidenceDuration.form.h2}
              >
                {t('CoapplicantResidenceDurationPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantResidenceDuration.alert.error
                    }
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  labelPlacement="below"
                  name="monthsAtResidenceValue"
                  options={[
                    {
                      label: t('CoapplicantResidenceDurationPage.yes'),
                      value: TWO_YEARS_IN_MONTHS_AS_STRING,
                      testId:
                        locator.pages.coapplicantResidenceDuration.form
                          .radioOption1,
                    },
                    {
                      label: t('CoapplicantResidenceDurationPage.no'),
                      value: ONE_YEAR_IN_MONTHS_AS_STRING,
                      testId:
                        locator.pages.coapplicantResidenceDuration.form
                          .radioOption2,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.coapplicantResidenceDuration.form
                      .radioGroupRoot
                  }
                  variant="inline"
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  data-testid={
                    locator.pages.coapplicantResidenceDuration.form.submitButton
                  }
                  disabled={isEmpty(coapplicantCurrentResidenceMonthsAt)}
                  loader={isSyncingCoapplicant}
                  fullWidth
                  submit
                >
                  {t('CoapplicantResidenceDurationPage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantResidenceDuration.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantResidenceDurationPage';
